<script setup>
import {onMounted, ref, watch} from 'vue';
import LineChart from '@/components/echarts/LineChart.vue';
import BarChart from '@/components/echarts/BarChart.vue';
import PieChart from '@/components/echarts/PieChart.vue';
import ClientService from '@/services/ClientService';
import router from '@/router';
import {useRoute} from 'vue-router';
import {jwtDecode} from 'jwt-decode';
import {getToken} from '@/services/AuthService';
import {areBondsEnabled} from '@/utils/features';
import Welcome from '@/views/Welcome.vue';
import ReportService from "@/services/ReportService";
import {formatDate, formatAmount, formatNumber, formatPercents, showSuccessMessage} from '../../helper';
import {useDataTable} from "@/utils/datatableHelper";
import {useI18n} from 'vue-i18n'
import * as XLSX from "xlsx";
import ReportsBonds from '@/components/bonds/ReportsBonds.vue';

const {t} = useI18n();

/**
 * IMA-517 fáze 8: záložky Investice | Dluhopisy. Vybraná záložka je v URL (?tab=bonds), aby přežila návrat
 * z detailu klienta. Dluhopisy se načtou až při prvním otevření a pak zůstávají vykreslené (v-show).
 */
const activeTab = ref(useRoute().query.tab === 'bonds' ? 'BONDS' : 'INVESTMENTS');
const bondsVisited = ref(activeTab.value === 'BONDS');
// v instanci bez dluhopisů (B.I.G. Invest SK/CZ) záložky nejsou – jen investice (i při ?tab=bonds v adrese)
const showBondsTab = ref(false);
areBondsEnabled().then(enabled => {
  showBondsTab.value = enabled;
  if (!enabled) {
    activeTab.value = 'INVESTMENTS';
    bondsVisited.value = false;
  }
});
watch(activeTab, tab => {
  if (tab === 'BONDS') {
    bondsVisited.value = true;
  }
  router.replace({query: {...router.currentRoute.value.query, tab: tab === 'BONDS' ? 'bonds' : undefined}});
});

// nastavení tabulky (stránka, počet řádků, řazení, filtry) se pamatuje v localStorage pod klíčem REPORTS-STATS
const datatableRef = ref(null);
const datatable = useDataTable(fetchStatsTableData, 'name', 20, true, {}, 'REPORTS-STATS', datatableRef);
const intermediaryPersonIdsPath = ref([{id: null, name: t('reportsIndex.all'), scope: 'intermediary_persons'}]);
const availablePeriods = ref([{label: t('reportsIndex.week'), value: 'week'}, {label: t('reportsIndex.month'), value: 'month'}, {label: t('reportsIndex.quarter'), value: 'quarter'}, {label: t('reportsIndex.year'), value: 'year'}]);

const filter = ref({
  from: null,
  to: null,
  period: 'months',
  intermediary_person_id: null
});

const filterFastOptions = ref([
  {
    label: t('reportsIndex.years'),
    value: 'years'
  },
  {
    label: t('reportsIndex.quarters'),
    value: 'quarters'
  },
  {
    label: t('reportsIndex.months'),
    value: 'months'
  },
  {
    label: t('reportsIndex.weeks'),
    value: 'weeks'
  },
  {
    label: t('reportsIndex.custom'),
    value: 'custom'
  }
]);
const filterFastOptionSelected = ref('months');

const columns = ref([
  {
    label: t('reportsIndex.name'),
    field: 'name',
    sortable: false,
  },
  {
    label: t('reportsIndex.contracts_count'),
    field: 'stats.contracts_count',
    sortable: false,
  },
  {
    label: t('reportsIndex.clients_count'),
    field: 'stats.clients_count',
    sortable: false,
  },
  {
    label: t('reportsIndex.contracts_amount'),
    field: 'stats.contracts_amount',
    sortable: false,
  },
  {
    label: t('reportsIndex.invested_amount'),
    field: 'stats.invested_amount',
    sortable: false,
  },
  {
    label: t('reportsIndex.contracts_count_structure'),
    field: 'stats.contracts_count_structure',
    sortable: false,
  },
  {
    label: t('reportsIndex.clients_count_structure'),
    field: 'stats.clients_count_structure',
    sortable: false,
  },
  {
    label: t('reportsIndex.contracts_amount_structure'),
    field: 'stats.contracts_amount_structure',
    sortable: false,
  },
  {
    label: t('reportsIndex.invested_amount_structure'),
    field: 'stats.invested_amount_structure',
    sortable: false,
  },
  {
    label: '',
    field: 'action',
    sortable: false,
    html: true
  },
]);

const query = useRoute().query;

function impersonator() {
  // getToken() vrací token aktivní pro tento panel - u impersonace jde o token
  // ze sessionStorage (localStorage nese pořád jen původní, neimpersonovanou identitu).
  const token = jwtDecode(getToken());
  if (!token || token.impersonator === null) {
    return false;
  }
  return token.impersonator;
}

function impersonateClient(client) {
  ClientService.impersonate(client.id).then(response => {
    showSuccessMessage(response.message);
  }, error => {
  });
}


function fetchStatsTableData(body) {
  ReportService.getStatsTable(body).then(response => {
    datatable.totalRecords.value = response.meta.total;
    datatable.rows.value = response.data;
    datatable.isLoading.value = false;
  });
}

function setFilterFastOption(filterFastOption) {
  if (isLoadingHistoryStats.value) {
    return;
  }
  filterFastOptionSelected.value = filterFastOption.value;
  switch (filterFastOption.value) {
    case 'years':
      filter.value.period = 'year';
      filter.value.from = null;
      filter.value.to = null;
      break;
    case 'quarters':
      filter.value.period = 'quarter';
      filter.value.from = null;
      filter.value.to = null;
      break;
    case 'months':
      filter.value.period = 'month';
      filter.value.from = null;
      filter.value.to = null;
      break;
    case 'weeks':
      filter.value.period = 'week';
      filter.value.from = null;
      filter.value.to = null;
      break;
    case 'custom':
      filter.value.period = 'quarter';
      filter.value.from = null;
      filter.value.to = null;
      break;
  }

  if (filterFastOption.value !== 'custom') {
    filterData();
  }
}


function showIntermediaryPersonDetail(id, name, scope) {
  // intermediaryPersonIdForTableStatsFilter.value = id;

  if (!scope) {
    scope = 'intermediary_persons';
  }

  datatable.filter.value.intermediary_person_id = id;
  datatable.filter.value.scope = scope;
  filter.value.intermediary_person_id = id;
  filter.value.scope = scope;

  // check if push or remove some parts...
  const existingIndex = intermediaryPersonIdsPath.value.findIndex(item => item.id === id && item.scope === scope);
  console.log(intermediaryPersonIdsPath);
  if (existingIndex !== -1) {
    intermediaryPersonIdsPath.value = intermediaryPersonIdsPath.value.slice(0, existingIndex + 1);
  } else {
    intermediaryPersonIdsPath.value.push({id: id, name: name, scope: scope});
  }
  loadStatsHistoryAmount();
  loadStatsHistoryContractsCount();
  loadStatsHistoryClientsCount();
}

function filterData() {
  isLoadingHistoryStats.value = true;
  loadStatsHistoryAmount();
  loadStatsHistoryContractsCount();
  loadStatsHistoryClientsCount();
}

function loadStatsHistoryAmount() {
  let body = {...{'filter': filter.value}, ...{'mode': 'amount'}};
  ReportService.getStatsHistory(body).then((response) => {
    statsHistoryAmount.value = response.data;
    isLoadingHistoryStats.value = false;
  });
}

function loadStatsHistoryContractsCount() {
  let body = {...{'filter': filter.value}, ...{'mode': 'contracts_count'}};
  ReportService.getStatsHistory(body).then((response) => {
    statsHistoryContractsCount.value = response.data;
    isLoadingHistoryStats.value = false;
  });
}

function loadStatsHistoryClientsCount() {
  let body = {...{'filter': filter.value}, ...{'mode': 'clients_count'}};
  ReportService.getStatsHistory(body).then((response) => {
    statsHistoryClientsCount.value = response.data;
    isLoadingHistoryStats.value = false;
  });
}


const statsHistoryContractsCount = ref(null);
const statsHistoryClientsCount = ref(null);
const statsHistoryAmount = ref(null);
const isLoadingTotalStats = ref(false);
const isLoadingHistoryStats = ref(false);
const isLoadingContactsTypesAmountStats = ref(false);
const isLoadingContactsTypesCountStats = ref(false);
const isLoadingStrategiesAmountStats = ref(false);
const isLoadingStrategiesCountStats = ref(false);

const totalStats = ref(null);
const contractsTypesAmountStats = ref(null);
const contractsTypesCountStats = ref(null);
const strategiesAmountStats = ref(null);
const strategiesCountStats = ref(null);


onMounted(async () => {

  isLoadingTotalStats.value = true;
  isLoadingContactsTypesAmountStats.value = true;
  isLoadingContactsTypesCountStats.value = true;
  isLoadingStrategiesAmountStats.value = true;
  isLoadingStrategiesCountStats.value = true;


  // ReportService.getTotalStats().then((response) => {
  //   totalStats.value = response.data;
  //   isLoadingTotalStats.value = false;
  // });
  // ReportService.getContractsTypesAmountStats().then((response) => {
  //   contractsTypesAmountStats.value = response.data;
  //   isLoadingContactsTypesAmountStats.value = false;
  // });
  // ReportService.getContractsTypesCountStats().then((response) => {
  //   contractsTypesCountStats.value = response.data;
  //   isLoadingContactsTypesCountStats.value = false;
  // });
  // ReportService.getStrategiesAmountStats().then((response) => {
  //   strategiesAmountStats.value = response.data;
  //   isLoadingStrategiesAmountStats.value = false;
  // });
  // ReportService.getStrategiesCountStats().then((response) => {
  //   strategiesCountStats.value = response.data;
  //   isLoadingStrategiesCountStats.value = false;
  // });

  isLoadingHistoryStats.value = true;
  loadStatsHistoryAmount();
  loadStatsHistoryContractsCount();
  loadStatsHistoryClientsCount();

})

function exportReport() {
  const fmt = (val, currency) => new Intl.NumberFormat("cs-CZ", { style: "currency", currency, maximumFractionDigits: 2 }).format(val ?? 0);

  const rows = [
    [
      t('reportsIndex.name'),
      t('reportsIndex.contracts_count'),
      t('reportsIndex.clients_count'),
      t('reportsIndex.contracts_amount'),
      t('reportsIndex.invested_amount'),
      t('reportsIndex.contracts_count_structure'),
      t('reportsIndex.clients_count_structure'),
      t('reportsIndex.contracts_amount_structure'),
      t('reportsIndex.invested_amount_structure'),
    ]
  ];

  const boldRows = [];

  const intermediaries = datatable.rows.value;
  intermediaries.forEach(intermediary => {
    boldRows.push(rows.length);
    rows.push([
      intermediary.name,
      intermediary.stats.contracts_count,
      '',
      fmt(intermediary.stats.contracts_amount, intermediary.stats.currency),
      fmt(intermediary.stats.invested_amount, intermediary.stats.currency),
      intermediary.stats.contracts_count_structure,
      '',
      fmt(intermediary.stats.contracts_amount_structure, intermediary.stats.currency),
      fmt(intermediary.stats.invested_amount_structure, intermediary.stats.currency),
    ]);

    intermediary.children?.forEach(child => {
      rows.push([
        '  → ' + child.name,
        child.stats.contracts_count,
        '',
        fmt(child.stats.contracts_amount, child.stats.currency),
        fmt(child.stats.invested_amount, child.stats.currency),
        child.stats.contracts_count_structure,
        '',
        fmt(child.stats.contracts_amount_structure, child.stats.currency),
        fmt(child.stats.invested_amount_structure, child.stats.currency),
      ]);
    });
  });

  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Styly pro tučné písmo
  const boldStyle = { font: { bold: true } };

  boldRows.forEach((rowIndex) => {
    const colCount = 9; // počet sloupců
    for (let col = 0; col < colCount; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: col });
      if (!ws[cellAddress]) continue;
      ws[cellAddress].s = boldStyle;
    }
  });

  //const worksheet = XLSX.utils.aoa_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, ws, t('reportsIndex.clients_and_intermediaries'));

  XLSX.writeFile(workbook, t('reportsIndex.export_file_name'), { cellStyles: true });
}

</script>
<template>
  <div>
    <ul v-if="showBondsTab" class="nav--tabs nav nav--pills mb-4" role="tablist">
      <li class="nav-item col-6" role="presentation">
        <button type="button" class="nav-link" :class="{active: activeTab === 'INVESTMENTS'}" role="tab" :aria-selected="activeTab === 'INVESTMENTS'" @click="activeTab = 'INVESTMENTS'">{{ t('reportsBonds.tab_investments') }}</button>
      </li>
      <li class="nav-item col-6" role="presentation">
        <button type="button" class="nav-link" :class="{active: activeTab === 'BONDS'}" role="tab" :aria-selected="activeTab === 'BONDS'" @click="activeTab = 'BONDS'">{{ t('reportsBonds.tab_bonds') }}</button>
      </li>
    </ul>
    <ReportsBonds v-if="showBondsTab && bondsVisited" v-show="activeTab === 'BONDS'"/>
    <div class="page-section" v-if="false">
      <div class="page-section__content">
                <span v-for="(intermediaryPersonId, intermediaryPersonIdIndex) in intermediaryPersonIdsPath" :key="intermediaryPersonId">
                    <span v-if="intermediaryPersonIdIndex === intermediaryPersonIdsPath.length - 1">
                        {{ intermediaryPersonId.name }}
                        <span v-if="intermediaryPersonId.scope === 'clients'" class="badge bg-secondary ms-2">{{ t('reportsIndex.clients') }}</span>
                    </span>
                    <span v-else>
                        <span @click="showIntermediaryPersonDetail(intermediaryPersonId.id, intermediaryPersonId.name)" class="me-2 page-section__heading__back">
                            {{ intermediaryPersonId.name }}
                        </span>
                        <span class="me-2">
                            <i class="mdi mdi-arrow-right"></i>
                        </span>
                    </span>
                </span>
      </div>
    </div>
    <div class="page-section" v-if="false">
      <div class="page-section__content">

        <div v-if="!isLoadingTotalStats && totalStats" class="row">
          <div class="col-3">
            <div class="card card--big-value">
              <div class="card-body">
                <span class="card__value__label">{{ t('reportsIndex.clients_count') }}</span>
                <div class="card__value">
                  {{ formatNumber(totalStats.clientsCount.value) }}
                </div>
              </div>
            </div>
          </div>
          <div class="col-3">
            <div class="card card--big-value">
              <div class="card-body">
                <span class="card__value__label">{{ t('reportsIndex.contracts_count') }}</span>
                <div class="card__value">
                  {{ formatNumber(totalStats.contractsCount.value) }}
                </div>
              </div>
            </div>
          </div>
          <div class="col-3">
            <div class="card card--big-value">
              <div class="card-body">
                <span class="card__value__label">{{ t('reportsIndex.total_invested') }}</span>
                <div class="card__value">
                  {{ formatAmount(totalStats.totalInvestedAmount.value, totalStats.totalInvestedAmount.currency) }}
                </div>
              </div>
            </div>
          </div>
          <div class="col-3">
            <div class="card card--big-value">
              <div class="card-body">
                <span class="card__value__label">{{ t('reportsIndex.portfolio_value') }}</span>
                <div class="card__value">
                  {{ formatAmount(totalStats.totalPortfolioAmount.value, totalStats.totalPortfolioAmount.currency) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="page-section mt-5" v-if="false">
      <div class="page-section__heading">
        <div class="page-section__title">
          {{ t('reportsIndex.types_of_contracts') }}
        </div>
      </div>
      <div class="page-section__content">

        <div class="row">
          <div class="col-6">
            <div v-if="!isLoadingContactsTypesAmountStats && contractsTypesAmountStats">
              <PieChart
                  :series="[
                                {
                                name: t('reportsIndex.contracts_amount'),
                                data: contractsTypesAmountStats
                                }
                            ]"
              ></PieChart>
            </div>
          </div>
          <div class="col-6">
            <div v-if="!isLoadingStrategiesCountStats && strategiesCountStats">
              <PieChart
                  :series="[
                                {
                                name: t('reportsIndex.contracts_count2'),
                                data: contractsTypesCountStats
                                }
                            ]"
              ></PieChart>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="page-section mt-5" v-if="false">
      <div class="page-section__heading">
        <div class="page-section__title">
          {{ t('reportsIndex.types_of_strategies') }}
        </div>
      </div>
      <div class="page-section__content">
        <div class="row">
          <div class="col-6">
            <div v-if="!isLoadingStrategiesAmountStats && strategiesAmountStats">
              <PieChart
                  :series="[
                                {
                                name: t('reportsIndex.strategies_amount'),
                                data: strategiesAmountStats
                                }
                            ]"
              ></PieChart>
            </div>
          </div>
          <div class="col-6">
            <div v-if="!isLoadingStrategiesCountStats && strategiesCountStats">
              <PieChart
                  :series="[
                                {
                                name: t('reportsIndex.strategies_count'),
                                data: strategiesCountStats
                                }
                            ]"
              ></PieChart>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="page-section mt-5" v-if="false">
      <div class="page-section__heading">
        <div class="page-section__title">
          {{ t('reportsIndex.historical_development') }}
        </div>
      </div>
      <div class="page-section__content">
        <div class="row">
          <div class="col" v-for="(filterFastOption) in filterFastOptions" :key="filterFastOption.value">
            <button class="btn d-block w-100" :class="{'btn-primary': filterFastOptionSelected === filterFastOption.value, 'btn-secondary': filterFastOptionSelected !== filterFastOption.value}" :disabled="isLoadingHistoryStats" @click="setFilterFastOption(filterFastOption)">
                            <span v-if="isLoadingHistoryStats && filterFastOptionSelected === filterFastOption.value">
                                <span class="spinner-border text-light" role="status" style="vertical-align: middle; position: relative; top: -3px;">
                                    <span class="visually-hidden">{{ t('reportsIndex.loading') }}</span>
                                </span>
                            </span>
              <span v-else>
                                {{ filterFastOption.label }}
                            </span>
            </button>
          </div>
        </div>
        <div :class="{'d-none': filterFastOptionSelected !== 'custom'}">
          <div class="row mt-4">
            <div class="col-4">
              <label class="form-label">{{ t('reportsIndex.period') }}</label>
              <select v-model="filter.period" class="w-100 form-select form-control">
                <option v-for="(availablePeriod) in availablePeriods" :key="availablePeriod" :value="availablePeriod.value">
                  {{ availablePeriod.label }}
                </option>
              </select>
            </div>
            <div class="col-8">
              <div class="row">
                <div class="col-6">
                  <div>
                    <label class="form-label">{{ t('reportsIndex.from') }}</label>
                    <input v-model="filter.from" class="form-control" type="date"/>
                  </div>
                </div>
                <div class="col-6">
                  <div>
                    <label class="form-label">{{ t('reportsIndex.to') }}</label>
                    <input v-model="filter.to" class="form-control" type="date"/>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-4">
            <div class="col-12 text-end">
              <button class="btn btn-primary" @click="filterData()">
                                <span v-if="isLoadingHistoryStats">
                                    <span class="spinner-border text-light" role="status" style="vertical-align: middle; position: relative; top: -3px;">
                                        <span class="visually-hidden">{{ t('reportsIndex.loading') }}</span>
                                    </span>
                                </span>
                <span v-if="!isLoadingHistoryStats">
                                    {{ t('reportsIndex.load') }}
                                </span>
              </button>
            </div>
          </div>
        </div>

        <div class="row mt-4" v-if="!isLoadingHistoryStats">
          <div class="col-lg-4">
            <div class="page-section__heading">
              <div class="page-section__subtitle">
                {{ t('reportsIndex.investment_amount') }}
              </div>
            </div>
            <div class="row" v-if="statsHistoryAmount">
              <div class="col-6">
                <div class="card card--big-value card--2-values">
                  <div class="card-body">
                    <div class="card__value">
                      {{ formatAmount(statsHistoryAmount.stats.startValue, statsHistoryAmount.meta.currency) }}
                    </div>
                    <div class="card__values_separator">
                      <i class="mdi mdi-chevron-down"></i>
                    </div>
                    <div class="card__value">
                      {{ formatAmount(statsHistoryAmount.stats.endValue, statsHistoryAmount.meta.currency) }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-6">
                <div class="card card--big-value card--2-values">
                  <div class="card-body">
                    <div class="card__value" :class="{'ci ci--up': statsHistoryAmount.stats.growthPercents > 0, 'ci ci--down': statsHistoryAmount.stats.growthPercents < 0}">
                      <span>{{ statsHistoryAmount.stats.growthPercents > 0 ? '+' : '' }}</span>{{ formatPercents(statsHistoryAmount.stats.growthPercents) }}
                    </div>
                    <div class="card__values_separator card__values_separator--line">
                      &nbsp;
                    </div>
                    <div class="card__value" :class="{'ci ci--up': statsHistoryAmount.stats.growth > 0, 'ci ci--down': statsHistoryAmount.stats.growth < 0}">
                      <span>{{ statsHistoryAmount.stats.growth > 0 ? '+' : '' }}</span>{{ formatAmount(statsHistoryAmount.stats.growth, statsHistoryAmount.meta.currency) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row mt-5" v-if="false">
              <div class="col-12">
                <div class="card card--flat">
                  <div class="card-body card-body--only-chart">
                    <div class="card-body__chart" v-if="statsHistoryAmount">
                      <BarChart
                          :series="statsHistoryAmount.series"
                          :valuesType="'amount'"
                          :currency="statsHistoryAmount.meta.currency"
                          :categories="statsHistoryAmount.categories"
                          :view="'full'"
                      ></BarChart>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-4">
            <div class="page-section__heading">
              <div class="page-section__subtitle">
                {{ t('reportsIndex.investment_count') }}
              </div>
            </div>
            <div class="row" v-if="statsHistoryContractsCount">
              <div class="col-6">
                <div class="card card--big-value card--2-values">
                  <div class="card-body">
                    <div class="card__value">
                      {{ formatNumber(statsHistoryContractsCount.stats.startValue, statsHistoryContractsCount.meta.currency) }}
                    </div>
                    <div class="card__values_separator">
                      <i class="mdi mdi-chevron-down"></i>
                    </div>
                    <div class="card__value">
                      {{ formatNumber(statsHistoryContractsCount.stats.endValue, statsHistoryContractsCount.meta.currency) }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-6">
                <div class="card card--big-value card--2-values">
                  <div class="card-body">
                    <div class="card__value" :class="{'ci ci--up': statsHistoryContractsCount.stats.growthPercents > 0, 'ci ci--down': statsHistoryContractsCount.stats.growthPercents < 0}">
                      <span>{{ statsHistoryContractsCount.stats.growthPercents > 0 ? '+' : '' }}</span>{{ formatPercents(statsHistoryContractsCount.stats.growthPercents) }}
                    </div>
                    <div class="card__values_separator card__values_separator--line">
                      &nbsp;
                    </div>
                    <div class="card__value" :class="{'ci ci--up': statsHistoryContractsCount.stats.growth > 0, 'ci ci--down': statsHistoryContractsCount.stats.growth < 0}">
                      <span>{{ statsHistoryContractsCount.stats.growth > 0 ? '+' : '' }}</span>{{ formatAmount(statsHistoryContractsCount.stats.growth, statsHistoryAmount.meta.currency) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row mt-5" v-if="false">
              <div class="col-12">
                <div class="card card--flat">
                  <div class="card-body card-body--only-chart">
                    <div class="card-body__chart" v-if="statsHistoryContractsCount">
                      <BarChart
                          :series="statsHistoryContractsCount.series"
                          :valuesType="'number'"
                          :categories="statsHistoryContractsCount.categories"
                          :view="'full'"
                      ></BarChart>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-4">
            <div class="page-section__heading">
              <div class="page-section__subtitle">
                {{ t('reportsIndex.clients_count') }}
              </div>
            </div>
            <div class="row" v-if="statsHistoryClientsCount">
              <div class="col-6">
                <div class="card card--big-value card--2-values">
                  <div class="card-body">
                    <div class="card__value">
                      {{ formatNumber(statsHistoryClientsCount.stats.startValue, statsHistoryClientsCount.meta.currency) }}
                    </div>
                    <div class="card__values_separator">
                      <i class="mdi mdi-chevron-down"></i>
                    </div>
                    <div class="card__value">
                      {{ formatNumber(statsHistoryClientsCount.stats.endValue, statsHistoryClientsCount.meta.currency) }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-6">
                <div class="card card--big-value card--2-values">
                  <div class="card-body">
                    <div class="card__value" :class="{'ci ci--up': statsHistoryClientsCount.stats.growthPercents > 0, 'ci ci--down': statsHistoryClientsCount.stats.growthPercents < 0}">
                      <span>{{ statsHistoryClientsCount.stats.growthPercents > 0 ? '+' : '' }}</span>{{ formatPercents(statsHistoryClientsCount.stats.growthPercents) }}
                    </div>
                    <div class="card__values_separator card__values_separator--line">
                      &nbsp;
                    </div>
                    <div class="card__value" :class="{'ci ci--up': statsHistoryClientsCount.stats.growth > 0, 'ci ci--down': statsHistoryClientsCount.stats.growth < 0}">
                      <span>{{ statsHistoryClientsCount.stats.growth > 0 ? '+' : '' }}</span>{{ formatAmount(statsHistoryClientsCount.stats.growth, statsHistoryAmount.meta.currency) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row mt-5" v-if="false">
              <div class="col-12">
                <div class="card card--flat">
                  <div class="card-body card-body--only-chart">
                    <div class="card-body__chart" v-if="statsHistoryClientsCount">
                      <BarChart
                          :series="statsHistoryClientsCount.series"
                          :valuesType="'number'"
                          :categories="statsHistoryClientsCount.categories"
                          :view="'full'"
                      ></BarChart>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-4" v-if="isLoadingHistoryStats">
          <div class="col-12 text-center">
            <div class="spinner-border text-gray" role="status">
              <span class="visually-hidden">{{ t('reportsIndex.loading') }}</span>
            </div>
          </div>
        </div>

      </div>
    </div>
    <div v-show="activeTab === 'INVESTMENTS'" class="page-section">
      <div class="page-section__heading">
        <div class="page-section__title">
          {{ t('reportsIndex.clients_and_intermediaries') }}
        </div>
      </div>
      <div class="page-section__content">
        <div class="row mt-5">
          <div class="col d-flex justify-content-end">
            <button class="btn btn-primary" @click="exportReport()">{{ t('reportsIndex.export_report') }}
            </button>
          </div>
        </div>
        <div class="row mt-5">
          <div class="col-12">
            <vue-good-table
                ref="datatableRef"
                :sort-options="{enabled: true, initialSortBy: datatable.initialSortBy.value}"
                :columns="columns"
                :rows="datatable.rows.value"
                mode="remote"
                @page-change="datatable.onPageChange"
                @sort-change="datatable.onSortChange"
                @column-filter="datatable.onColumnFilter"
                @per-page-change="datatable.onPerPageChange"
                :totalRows="datatable.totalRecords.value"
                :group-options="{
                            enabled: true,
                            headerPosition: 'top'
                        }"
                :pagination-options="{
                            enabled: true,
                            rowsPerPageLabel: t('reportsIndex.entriesPerPage'),
                            nextLabel: t('reportsIndex.next'),
                            prevLabel: t('reportsIndex.previous'),
                            ofLabel: 'z',
                        }">
              <template #table-header-row="props">
                <span v-if="props.column.field=='stats.contracts_amount'">
                  <span>{{ formatAmount(props.row.stats.contracts_amount, props.row.stats.currency) }}</span>
                  <br/>
                  <small class="text-muted">
                    <i class="mdi mdi-chart-line me-1"/>{{ formatAmount(props.row.stats.sec_amount, props.row.stats.currency) }}<br />
                    <i class="mdi mdi-cash me-1"/>{{ formatAmount(props.row.stats.cash_amount, props.row.stats.currency) }}
                  </small>
                </span>
                <span v-if="props.column.field=='stats.invested_amount'">
                  <span>{{ formatAmount(props.row.stats.invested_amount, props.row.stats.currency) }}</span>
                </span>
                <span v-if="props.column.field=='stats.contracts_amount_structure'">
                  <span>{{ formatAmount(props.row.stats.contracts_amount_structure, props.row.stats.currency) }}</span>
                  <br/>
                  <small class="text-muted">
                    <i class="mdi mdi-chart-line me-1"/>{{ formatAmount(props.row.stats.sec_amount_structure, props.row.stats.currency) }}<br />
                    <i class="mdi mdi-cash me-1"/>{{ formatAmount(props.row.stats.cash_amount_structure, props.row.stats.currency) }}
                  </small>
                </span>
                <span v-if="props.column.field=='stats.invested_amount_structure'">
                  <span>{{ formatAmount(props.row.stats.invested_amount_structure, props.row.stats.currency) }}</span>
                </span>
                <span v-if="props.column.field == 'action'">
                  <span @click="showIntermediaryPersonDetail(props.row.id, props.row.name, 'clients')" v-if="props.row.type == 'INTERMEDIARY_PERSON'">
                    <button type="button" class="btn btn-link"><i class="mdi mdi-eye"/></button>
                  </span>
                </span>
              </template>
              <template #table-row="props">
                            <span v-if="props.column.field == 'name'">
                                    <span v-if="props.row.type === 'CLIENT' && !impersonator()" @click="impersonateClient(props.row)" style="cursor: pointer;">
                                        {{ props.row.name }} <i class="mdi mdi-login"></i>
                                    </span>
                                    <span v-else>
                                        {{ props.row.name }}
                                    </span>
                                <br/>
                                <small>{{ t('reportsIndex.entity_type_' + props.row.type) }}</small>
                            </span>
                <span v-if="props.column.field == 'action'">
                                <span @click="showIntermediaryPersonDetail(props.row.id, props.row.name)" v-if="props.row.type != 'CLIENT'">
                                    <button type="button" class="btn btn-link"><i class="mdi mdi-eye"/></button>
                                </span>
                                <span v-if="props.row.type === 'CLIENT'">
                                    <router-link :to="{ name: 'ClientDetail', params: { id: props.row.id} }"><button type="button" class="btn btn-link"><i class="mdi mdi-eye"/></button></router-link>
                                </span>
                            </span>
                <span v-if="props.column.field=='stats.contracts_amount'">
                  <span>{{ formatAmount(props.row.stats.contracts_amount, props.row.stats.currency) }}</span>
                  <br/>
                  <small class="text-muted">
                    <i class="mdi mdi-chart-line me-1"/>{{ formatAmount(props.row.stats.sec_amount, props.row.stats.currency) }}<br />
                    <i class="mdi mdi-cash me-1"/>{{ formatAmount(props.row.stats.cash_amount, props.row.stats.currency) }}
                  </small>
                </span>
                <span v-if="props.column.field=='stats.invested_amount'">
                  <span>{{ formatAmount(props.row.stats.invested_amount, props.row.stats.currency) }}</span>
                </span>
                <span v-if="props.column.field=='stats.contracts_amount_structure'">
                  <span>{{ formatAmount(props.row.stats.contracts_amount_structure, props.row.stats.currency) }}</span>
                  <br/>
                  <small class="text-muted">
                    <i class="mdi mdi-chart-line me-1"/>{{ formatAmount(props.row.stats.sec_amount_structure, props.row.stats.currency) }}<br />
                    <i class="mdi mdi-cash me-1"/>{{ formatAmount(props.row.stats.cash_amount_structure, props.row.stats.currency) }}
                  </small>
                </span>
                <span v-if="props.column.field=='stats.invested_amount_structure'">
                  <span>{{ formatAmount(props.row.stats.invested_amount_structure, props.row.stats.currency) }}</span>
                </span>
              </template>
              <template v-slot:emptystate v-if="!datatable.isLoading.value">
                <div class="text-center">
                  {{ t('clientIndex.no_records_found') }}
                </div>
              </template>
              <template v-slot:emptystate v-if="datatable.isLoading.value">
                <div class="text-center">
                  {{ t('clientIndex.loading') }}
                </div>
              </template>
            </vue-good-table>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

